<script setup lang="ts">
import type { NuxtError } from '#app';

/**
 * Global Fullscreen Error Page
 */

const { error } = defineProps<{
    error: NuxtError;
}>();

const { t: $t, locales } = useNuxtApp().$i18n;
const route = useRoute();
const is404 = computed(() => error.status === 404);
const title = computed(() => $t(`core.errorMessages.${is404.value ? '404' : '500'}`));
const productKeys: readonly string[] = PRODUCT_KEYS.filter(key => !['activity', 'p2p'].includes(key));

onMounted(() => {
    analyticsTrackError(String(error.status), route.fullPath, title.value);
});

const cta = computed(() => {
    const offerFlowStartPath = is404.value ? getOfferFlowStartPath() : undefined;

    return {
        action: offerFlowStartPath
            ? () => navigateTo(offerFlowStartPath)
            : () => window.location.reload(),
        label: $t('core.buttons.retry'),
    };
});

function getOfferFlowStartPath() {
    const pathSegments = route.path.split('/');
    const locale = locales.value.find(locale => locale.code === pathSegments[1])?.code;
    const flowTypeIndex = locale ? 2 : 1;
    const productKey = pathSegments[flowTypeIndex + 1];

    if (pathSegments[flowTypeIndex] === 'booking' && productKey && productKeys.includes(productKey)) {
        return `/${locale ?? 'de'}/booking/${productKey}/start`;
    }

    return undefined;
}
</script>

<template>
    <div class="flex min-h-dvh items-center justify-center bg-brand-b px-container-gap text-white">
        <TheLogo class="absolute top-5 left-container-gap md:top-7" />
        <PaneProse class="text-center">
            <h1 class="mb-2">
                {{ title }}
            </h1>
            <p v-if="!is404">
                {{ error.message }}
            </p>
            <BaseButton class="mx-auto mt-8 block" @click="cta.action">
                {{ cta.label }}
            </BaseButton>
        </PaneProse>
    </div>
</template>
